import { SimSearchOptions } from '../api/utilities';
import { HashMap } from '../constants';
import { SimManagementError } from './Errors';
import { Provisioning } from './provision';
import { QueryResult } from './queryResult';
import { BasicPageableSearchFilter } from './searchFilter';
import { ProductOrder, ProductOrderState } from './state/bucket';
import { ProvisioningState } from './state/simsState';

export enum LegacySimStatus {
  other = 'Other',
  availableForHire = 'Available for Hire',
  onHire = 'On Hire',
  awaitingRA = 'Awaiting RA',
  notYetConnected = 'Not Yet Connected',
  migrateInStarted = 'Migrate-In Started',
  portInStarted = 'Port-In Started'
}

export enum SimStatus {
  provisioned = 'Provisioned',
  notProvisioned = 'Not provisioned',
  provisionedActive = 'Provisioned (active)',
  provisionedInactive = 'Provisioned (inactive)',
  notProvisionedActive = 'Not provisioned (active)',
  notProvisionedInactive = 'Not provisioned (inactive)',
  portInStarted = 'Port-in started',
  awaitingProvision = 'Provisioned - Awaiting Tariff',
  other = 'Other'
}

export interface SimInfo {
  companyId: number;
  accountId: number;
  raNumber: number;
  serialNumber: string;
  stockId: number;
  prefix?: string | null;
  tel: string;
  expired?: boolean;
  orangeProductOrder?: ProductOrderState;
  provisioning?: ProvisioningState;
  eSimUrl?: string;
  iccid?: string;
}

export interface SmsHistoryResponse {
  data: SmsHistory[];
  total: number;
  totalPages: number;
}

export interface SmsHistory {
  inbound: boolean;
  message: string;
  binary: boolean;
  deliveryStatus?: string;
  createdDateUtc: string;
}

export interface Sim extends SimInfo {
  commencementDate?: Date;
  accountId: number;
  accountTitle: string;
  tariff: string;
  network: string;
  networkId: number;
  puk: string;
  pin: string;
  order: string;
  order1: string;
  order2: string;
  order3: string;
  order4: string;
  order5: string;
  lastCommand: string;
  provisioningOrder: string;
  legacyStatus: string;
  status: string;
  selected?: boolean;
  portingInfo?: { [key: string]: string };
  simType?: string | null;
  alerts?: SimAlertStatus[];
  activeBundle?: boolean;
  dataBundleMb?: number;
  dataUsageMb?: number;
  operationInProgress?: boolean;
  privateIp?: string | null;
  publicIp?: string | null;
  inSession?: boolean | null;
  simStatus?: string | null;
  imei?: string;
  alertManagerEnabled: boolean;
  smsCount?: number;
  voiceMinutes?: number;
  subportal?: string | null;
  tags?: SimTag[];
}

export interface SimTag {
  id: string;
  name: string;
  colour: string;
}

export interface SimAlertStatus {
  alertType: string;
  alertStatus: string;
  triggerDateTime: string;
  triggerLevelSummary?: string;
  triggerLevelDetail?: string;
}

export interface ProvisionListItem extends SimInfo {
  tel: string;
}

export interface SimSwapListItem extends SimInfo {
  swapSerialNumber: string;
}

export interface MsisdnSwapListItem extends SimInfo {
  newMsisdn: string;
  networkId?: number;
}

/**
 * Holds information about a successful load of a SIM's orange product order from the server.
 */
export interface LoadBucketSuccessResponse {
  stockId: number;
  productOrder: ProductOrder;
}

/**
 * Holds information about a successful load of a SIM's provisioning info from the server.
 */
export interface LoadProvisioningSuccessResponse {
  stockId: number;
  provisioning: Provisioning;
}

/**
 * Holds information about a failure to load SIM provisioning information from the server.
 */
export interface LoadStockInfoFailurePayload {
  stockId: number;
  error: SimManagementError;
}

/**
 * Holds information about a successful load of SIMs from the server.
 */
export interface LoadSimsSuccessPayload {
  searchFilter: BasicPageableSearchFilter;
  sims: QueryResult<Sim>;
}

/**
 * Holds information about a failure to load SIMs from the server.
 */
export interface LoadSimsFailurePayload {
  searchFilter: BasicPageableSearchFilter;
  error: SimManagementError;
}

export function getDefaultSim(): Sim {
  return {
    stockId: 0,
    companyId: 0,
    serialNumber: '',
    raNumber: 0,
    tel: '',
    accountId: 0,
    accountTitle: '',
    tariff: '',
    network: '',
    networkId: 0,
    puk: '',
    pin: '',
    order: '',
    order1: '',
    order2: '',
    order3: '',
    order4: '',
    order5: '',
    lastCommand: '',
    provisioningOrder: '',
    legacyStatus: '',
    status: '',
    eSimUrl: undefined,
    iccid: undefined,
    activeBundle: false,
    inSession: null,
    simType: null,
    dataBundleMb: undefined,
    dataUsageMb: undefined,
    operationInProgress: undefined,
    privateIp: null,
    publicIp: null,
    simStatus: null,
    alerts: [],
    alertManagerEnabled: false
  };
}

export interface SimColumn {
  field: string;
  title: string;
  width: number;
}

export interface SimColumnV2 {
  key: string;
  name: string;
  width: number;
  draggable: boolean;
  resizable: boolean;
  formatter?: any;
}

export interface SimsSuccessResponse extends Partial<SimSearchOptions> {
  totalCount?: number;
  searchFilter: BasicPageableSearchFilter;
  sims: QueryResult<Sim>;
}

export interface SimPermissions {
  canBarUnbar: boolean;
  canSimSwap: boolean;
  canProvision: boolean;
  canUnprovision: boolean;
  canChangeProvision: boolean;
  canSetSmsAlerts: boolean;
  loaded: boolean;
}

export interface IotSimInfo {
  ipAddress?: string;
  lockedImei?: string;
  imeiLocked?: boolean;
  imeiLockNotifyEmailAddresses?: Array<string>;
  lastKnownImei?: string;
  publicIp?: boolean;
  apn?: string;
  isIot: boolean;
  ipAddressType?: string;
  imeiLockTriggeredDate?: string; // DateTime
  staticIPGroupId?: number;
  publicIPGroupId?: number;
}

export function getSelectedStockSerialNumbers(selected: HashMap<SimInfo>, includeExpired: boolean) {
  const selectedSerialNumbers: string[] = [];
  for (const property in selected) {
    if (selected.hasOwnProperty(property)) {
      const value = selected[property];
      if (value && (includeExpired || !value.expired)) {
        selectedSerialNumbers.push(value.serialNumber);
      }
    }
  }

  selectedSerialNumbers.sort();
  return selectedSerialNumbers;
}

// one entry per selected SIM, for counting how much of the selection carries a given tag. The store
// is typed to SimInfo but holds whole grid rows, so the tags are asked for rather than assumed
export function getSelectedSimTags(selected: HashMap<SimInfo & { tags?: SimTag[] }>, includeExpired: boolean) {
  const selectedTags: SimTag[][] = [];
  for (const property in selected) {
    if (selected.hasOwnProperty(property)) {
      const value = selected[property];
      if (value && (includeExpired || !value.expired)) {
        selectedTags.push(value.tags ?? []);
      }
    }
  }

  return selectedTags;
}

export function getExpiredSelectedSims(selected: HashMap<SimInfo>) {
  const selectedSerialNumbers: SimInfo[] = [];
  for (const property in selected) {
    if (selected.hasOwnProperty(property)) {
      const value = selected[property];
      if (value && value.expired) {
        selectedSerialNumbers.push(value);
      }
    }
  }

  return selectedSerialNumbers;
}

export function getExpiredSelectedStockIds(selected: HashMap<SimInfo>) {
  const selectedSerialNumbers: number[] = [];
  for (const property in selected) {
    if (selected.hasOwnProperty(property)) {
      const value = selected[property];
      if (value && value.expired) {
        selectedSerialNumbers.push(value.stockId);
      }
    }
  }

  return selectedSerialNumbers;
}
